// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import BackdropProgress from "../../BackdropProgress";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClearIcon from "@material-ui/icons/Clear";
import CustomSnackbar from "../ui/Snackbars";
import EnhancedTableHead from "../ui/EnhancedTableHead";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import SearchIcon from "@mui/icons-material/Search";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import useHttp from "../../utils/http";
import wso2Theme from "../../theme";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  adjustForTimezone,
  getDateForDB,
  getCountryList,
  truncateString,
  getComparator,
  stableSort,
} from "../../utils/formatting";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  getEmployeeEmails,
  getUserName,
  isAccessGranted,
} from "../../utils/oauth";
import { travelStatus, travelStatusArray } from "../../utils/const";
import {
  postSearchTravelRequests,
  getTravelAgents,
} from "../../utils/end-points.js";
import {
  getTravelStatusColor,
  getTravelStatusFontColor,
} from "../../theme/status-colors";
import { IconButton } from "@material-ui/core";
import {
  APP_CONFIG,
  NO_VALID_VISA_AVAILABLE_ID,
  PRIVILEGES,
  VISA_NOT_APPLICABLE_ID,
} from "../../Config";
import { useEffect, useState, useCallback } from "react";
import "../../css/MyTravelRequests.scss";
import "../../utils/end-points.js";

const headCells = [
  {
    id: "job_number",
    label: "Job Number",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "traveller_email",
    label: "Traveller's Email",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "country",
    label: "Destination Country",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "city",
    label: "Travel Location",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "start_date",
    label: "Start Date",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "end_date",
    label: "End Date",
    align: "left",
    disablePadding: false,
    sortable: true,
  },
  {
    id: "travel_agent_id",
    label: "Travel Agent",
    align: "left",
    disablePadding: false,
    sortable: false,
  },
  {
    id: "visa_id",
    label: "Visa Information",
    align: "left",
    disablePadding: false,
    sortable: false,
  },
  {
    id: "status",
    label: "Travel Status",
    align: "left",
    disablePadding: false,
    sortable: false,
  },
];

const MyTravelRequests = ({ history }) => {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("start_date");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [travelRequests, setTravelRequests] = useState([]);
  const [travelAgents, setTravelAgents] = useState([]);
  const [countryFilter, setCountryFilter] = useState("");
  const [travelAgentFilter, setTravelAgentFilter] = useState(null);
  const [travelStatusFilter, setTravelStatusFilter] = useState("all");
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [searchFilter, setSearchFilter] = useState(null);
  const [selectedTravel, setSelectedTravel] = useState(null);
  const [isPageLoading, setPageLoading] = useState(true);
  const [isTableLoading, setTableLoading] = useState(true);
  const [isTablePaginating, setTablePaginating] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });
  const { handleRequest } = useHttp();

  useEffect(() => {
    fetchTravelAgents(rowsPerPage);
  }, []);

  useEffect(() => {
    fetchTravels();
  }, [
    rowsPerPage,
    travelAgents,
    countryFilter,
    travelAgentFilter,
    travelStatusFilter,
    startDateFilter,
    endDateFilter,
    orderBy,
    order,
    travelAgents,
  ]);

  useEffect(() => {
    if (window.innerHeight > 900) {
      setRowsPerPage(15);
    }
  }, [window.innerHeight]);

  const fetchTravelAgents = useCallback(async (limit) => {
    setPageLoading(true);
    handleRequest(
      getTravelAgents + "?status=all",
      "GET",
      null,
      (travelAgents) => {
        if (travelAgents) {
          setTravelAgents(travelAgents);
        } else {
          setSnackbarData({
            message: "Error while fetching travel agents",
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
        setPageLoading(false);
      },
      (error) => {
        setPageLoading(false);
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    );
  }, []);

  const fetchTravels = useCallback(async () => {
    setTableLoading(true);
    if (isAccessGranted([PRIVILEGES.MY_TRAVEL_REQUESTS])) {
      let url = postSearchTravelRequests;
      let body = {
        status: travelStatusFilter ? travelStatusFilter : "all",
        limit: rowsPerPage,
        offset: 0,
        searchString: searchFilter,
        email: getUserName(),
        country: countryFilter?.country,
        travelAgentId: travelAgentFilter,
        startDate: getDateForDB(startDateFilter),
        endDate: getDateForDB(endDateFilter),
        sortColumn: orderBy,
        sortOrder: order,
      };
      handleRequest(
        url,
        "POST",
        body,
        (travels) => {
          if (travels) {
            travels.map((travel) => {
              let travelAgentName =
                travelAgents.filter(
                  (agent) => agent.id === travel.travelAgentId
                ).length > 0
                  ? travelAgents.filter(
                      (agent) => agent.id === travel.travelAgentId
                    )[0].nameOfTravelAgent
                  : "";
              travel.travelAgentName = travelAgentName;
            });
            setTravelRequests(travels);
            setSelectedTravel(travels[0]);
            setPage(0);
          } else {
            setSnackbarData({
              message: "Error while fetching travel requests",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
          setTableLoading(false);
        },
        (error) => {
          setTableLoading(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    } else {
      setTravelRequests([]);
      setTableLoading(false);
      setSnackbarData({
        message: "You don't have permission to get travels",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    }
  }, [
    rowsPerPage,
    countryFilter,
    searchFilter,
    travelAgentFilter,
    travelStatusFilter,
    startDateFilter,
    endDateFilter,
    orderBy,
    order,
    travelAgents,
  ]);

  const loadMoreTravels = (offset) => {
    setTablePaginating(true);

    if (isAccessGranted([PRIVILEGES.MY_TRAVEL_REQUESTS])) {
      let url = postSearchTravelRequests;

      let body = {
        status: travelStatusFilter ? travelStatusFilter : "all",
        limit: rowsPerPage,
        offset: offset,
        searchString: searchFilter,
        email: getUserName(),
        country: countryFilter?.country,
        travelAgentId: travelAgentFilter,
        startDate: getDateForDB(startDateFilter),
        endDate: getDateForDB(endDateFilter),
        sortColumn: orderBy,
        sortOrder: order,
      };

      handleRequest(
        url,
        "POST",
        body,
        (travels) => {
          if (travels) {
            travels.map((travel) => {
              let travelAgentName =
                travelAgents.filter(
                  (agent) => agent.id === travel.travelAgentId
                ).length > 0
                  ? travelAgents.filter(
                      (agent) => agent.id === travel.travelAgentId
                    )[0].nameOfTravelAgent
                  : "";
              travel.travelAgentName = travelAgentName;
            });

            let travelsList = travelRequests.concat(travels);
            setTravelRequests(travelsList);
            if (travels.length === 0) {
              setPage((page) => page - 1);
              setSnackbarData({
                message: "No more travel requests",
                open: true,
                severity: "success",
                onClose: onCloseSnackbar,
              });
            }
          } else {
            setSnackbarData({
              message: "Error while fetching travel requests",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
          }
          setTablePaginating(false);
        },
        (error) => {
          setTablePaginating(false);
          setSnackbarData({
            message: error,
            open: true,
            severity: "error",
            onClose: onCloseSnackbar,
          });
        }
      );
    } else {
      setTravelRequests([]);
      setTablePaginating(false);
      setSnackbarData({
        message: "You don't have permission to get travels",
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClick = (travel) => {
    setSelectedTravel(travel);
    history.push(APP_CONFIG.PAGES.EDIT_TRAVEL_REQUEST + "/" + travel.jobNumber);
  };

  const handleChangePage = (event, newPage) => {
    if (travelRequests.length <= newPage * rowsPerPage) {
      loadMoreTravels(travelRequests.length);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCountryFilter = (event, value) => {
    setCountryFilter(value);
  };

  const handleTravelAgentFilter = (event, value) => {
    setTravelAgentFilter(value ? value.id : null);
  };

  const handleTravelStatusFilter = (event, value) => {
    setTravelStatusFilter(value ? value : "");
  };

  const handleSearchFilter = (event) => {
    setSearchFilter(event.target.value)
    if (!event.target.value) {
        fetchTravels()
    }
}

  const getVisaInfo = (travel) => {
    if (travel.visaId) {
      if (travel.visaId === NO_VALID_VISA_AVAILABLE_ID.id) {
        return <span>No Valid Visa Available</span>;
      } else if (travel.visaId === VISA_NOT_APPLICABLE_ID.id) {
        return <span>Visa Not Applicable</span>;
      } else {
        return <span>Visa Available</span>;
      }
    } else if (travel.visa) {
      if (
        travel.visa === "Visa Not Applicable" ||
        travel.visa === "Valid Visa Available"
      ) {
        return <span>{travel.visa}</span>;
      } else {
        return <span>Visa Available</span>;
      }
    } else {
      return "";
    }
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  return (
    <div className="My-travel-requests">
      <CustomSnackbar snackbar={snackbarData} />
      <BackdropProgress open={isPageLoading} />
      <div className="Page-title">My Travel Requests</div>
      <React.Fragment>
        {!isPageLoading && (
          <Grid container>
            <Grid item lg={12} className="Table-grid">
              <Paper className="Border-elevation">
                <div
                  className="Search-and-filters"
                  style={{ borderColor: wso2Theme.palette.primary.light }}
                >
                  <div className="Table-search-field">
                    <TextField
                      variant="outlined"
                      label="Search"
                      size="small"
                      className="Search-text-fields"
                      onChange={handleSearchFilter}
                    />

                    <Button
                      variant={"contained"}
                      startIcon={<SearchIcon />}
                      color="secondary"
                      className="Filter-button"
                      onClick={fetchTravels}
                    >
                      Search
                    </Button>
                    <Button
                      variant={filtersExpanded ? "contained" : "outlined"}
                      startIcon={<FilterAltIcon />}
                      color="primary"
                      className="Filter-button"
                      onClick={() => setFiltersExpanded(!filtersExpanded)}
                    >
                      {filtersExpanded ? "Hide Filters" : "Show Filters"}
                    </Button>
                  </div>
                  {filtersExpanded && (
                    <Grid container className="Filters-div">
                      <Grid
                        item
                        lg={3}
                        sx={{ flexBasis: { md: "20%" } }}
                        className="Filter-grid-item-country"
                      >
                        <Autocomplete
                          size="small"
                          disablePortal
                          options={getCountryList()}
                          getOptionLabel={(option) => option.country}
                          onChange={handleCountryFilter}
                          renderOption={(props, option) => (
                            <Box {...props}>{option.country}</Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="Country" />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        sx={{ flexBasis: { md: "20%" } }}
                        className="Filter-grid-item-agent"
                      >
                        <Autocomplete
                          loading
                          disabled={
                            !isAccessGranted([PRIVILEGES.MY_TRAVEL_REQUESTS])
                          }
                          size="small"
                          disablePortal
                          options={travelAgents}
                          getOptionLabel={(option) => option.nameOfTravelAgent}
                          onChange={handleTravelAgentFilter}
                          renderOption={(props, option) => (
                            <Box {...props}>{option.nameOfTravelAgent}</Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="Travel Agent" />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        sx={{ flexBasis: { md: "20%" } }}
                        className="Filter-grid-item-status"
                      >
                        <Autocomplete
                          size="small"
                          disablePortal
                          options={travelStatusArray}
                          getOptionLabel={(status) => status}
                          onChange={handleTravelStatusFilter}
                          renderOption={(props, status) => (
                            <Box {...props}>
                              <span
                                className="Status-dot-dropdown"
                                style={{
                                  backgroundColor: getTravelStatusColor(status),
                                }}
                              ></span>
                              {status}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} label="Travel Status" />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        sx={{ flexBasis: { md: "20%" } }}
                        className="Filter-grid-item-start-date"
                      >
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="Start Date"
                            inputFormat="yyyy-MM-dd"
                            mask="____-__-__"
                            value={startDateFilter}
                            onChange={(newValue) => {
                              setStartDateFilter(newValue);
                            }}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  onClick={() => setStartDateFilter(null)}
                                  size="small"
                                >
                                  <ClearIcon />
                                </IconButton>
                              ),
                            }}
                            InputAdornmentProps={{
                              position: "start",
                            }}
                            renderInput={(params) => (
                              <TextField size="small" {...params} fullWidth />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid
                        item
                        lg={3}
                        sx={{ flexBasis: { md: "20%" } }}
                        className="Filter-grid-item-end-date"
                      >
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label="End Date"
                            inputFormat="yyyy-MM-dd"
                            mask="____-__-__"
                            value={endDateFilter}
                            onChange={(newValue) => {
                              setEndDateFilter(newValue);
                            }}
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  onClick={() => setEndDateFilter(null)}
                                  size="small"
                                >
                                  <ClearIcon />
                                </IconButton>
                              ),
                            }}
                            InputAdornmentProps={{
                              position: "start",
                            }}
                            renderInput={(params) => (
                              <TextField size="small" {...params} fullWidth />
                            )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  )}
                </div>
                {isTableLoading && <LinearProgress color="secondary" />}
                <TableContainer className="Table-container">
                  <Table aria-labelledby="tableTitle" size={"small"}>
                    <EnhancedTableHead
                      headCells={headCells}
                      order={order}
                      orderBy={orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {selectedTravel &&
                        stableSort(
                          travelRequests,
                          getComparator(order, orderBy)
                        )
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((row) => {
                            return (
                              <TableRow
                                onClick={(event) => handleClick(row)}
                                tabIndex={-1}
                                key={row.jobNumber}
                                className="Selected-row"
                                style={{
                                  backgroundColor:
                                    selectedTravel.jobNumber === row.jobNumber
                                      ? "white"
                                      : null,
                                  elevation:
                                    selectedTravel.jobNumber === row.jobNumber
                                      ? "above"
                                      : null,
                                }}
                              >
                                <TableCell className="Table-cell Table-cell-job-number">
                                  {truncateString(row.jobNumber, 20)}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-email">
                                  {truncateString(row.travellerEmail, 20)}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-country">
                                  {truncateString(row.country, 20)}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-location">
                                  {truncateString(row.city, 17)}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-date">
                                  {getDateForDB(
                                    adjustForTimezone(new Date(row.startDate))
                                  )}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-date">
                                  {getDateForDB(
                                    adjustForTimezone(new Date(row.endDate))
                                  )}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-agent">
                                  {truncateString(row.travelAgentName, 20)}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-visa">
                                  {getVisaInfo(row)}
                                </TableCell>
                                <TableCell className="Table-cell Table-cell-status">
                                  <span
                                    className="Status-chip"
                                    style={{
                                      backgroundColor: getTravelStatusColor(
                                        row.status
                                      ),
                                      color: getTravelStatusFontColor(
                                        row.status
                                      ),
                                    }}
                                  >
                                    {row.status}
                                  </span>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!isTableLoading &&
                  !isTablePaginating &&
                  travelRequests.length === 0 && (
                    <div className="No-requests-label">No Travel Requests</div>
                  )}
                {(isTableLoading || isTablePaginating) && (
                  <div className="No-requests-label">Please wait ...</div>
                )}
                {isTablePaginating && <LinearProgress color="secondary" />}

                <TablePagination
                  className="Table-pagination"
                  rowsPerPageOptions={[10, 15, 25, 50]}
                  component="div"
                  count={-1}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Grid>
          </Grid>
        )}
      </React.Fragment>
    </div>
  );
};

export default MyTravelRequests;
